import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Menu, LogOut, Settings, Package, Folder, FileText, ChevronRight, Home, Sparkles, ExternalLink, Map, PenTool, BarChart3 } from 'lucide-react';
import { useUserStore } from '../../stores/user-store';
import { cn } from '../../utils/cn';
import SearchModal from '../search/SearchModal';
import MobileSearchModal from '../search/MobileSearchModal';
import { useAuthStore } from '../../stores/auth-store';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFileStore } from '../../stores/file-store';
import { useCategoryStore } from '../../stores/category-store';
import { useI18n } from '../../i18n';
import { supabase } from '../../config/supabase';
import { useToast } from '../shared/ToastContainer';
import { useUIStore, FILE_TABS } from '../../stores/ui-store';

interface HeaderNewProps {
  toggleSidebar: () => void;
  sidebarCollapsed: boolean;
  toggleMobileSidebar: () => void;
  mobileSidebarOpen: boolean;
}

// 搜索建议类型定义
interface SearchSuggestion {
  id: string;
  type: 'order' | 'folder' | 'file';
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  data: any;
}

// 智能工具列表
const SMART_TOOLS = [
  {
    id: 'poster',
    name: '产品地图',
    description: '拖拽式产品矩阵设计与导出',
    icon: Map,
    path: '/poster',
    gradient: 'from-sky-500 to-blue-600',
    bgLight: 'bg-sky-50',
  },
  {
    id: 'whiteboard',
    name: '协作白板',
    description: 'Excalidraw 自由画布与协作',
    icon: PenTool,
    path: '/whiteboard',
    gradient: 'from-violet-500 to-purple-600',
    bgLight: 'bg-violet-50',
  },
  {
    id: 'price-intel',
    name: '竞品价格情报',
    description: '解析报价资料、审核事实、生成趋势与汇报',
    icon: BarChart3,
    path: '/price_intel',
    gradient: 'from-amber-500 to-rose-500',
    bgLight: 'bg-amber-50',
  },
];

const HeaderNew: React.FC<HeaderNewProps> = ({ toggleSidebar, toggleMobileSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const { signOut } = useAuthStore();
  const { profile } = useUserStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const toolsMenuRef = React.useRef<HTMLDivElement>(null);
  const toolsButtonRef = React.useRef<HTMLButtonElement>(null);
  const toast = useToast();

  // 智能搜索状态
  const [smartSearchQuery, setSmartSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const { categories, activeCategory } = useCategoryStore();
  const { t, lang, switchTo } = useI18n();
  const [isDesktop, setIsDesktop] = useState<boolean>(true);

  // 搜索相关引用
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionBoxRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 获取全局文件类型 Tab 状态
  const { activeFileTab, setActiveFileTab } = useUIStore();

  React.useEffect(() => {
    useUserStore.getState().fetchProfile();
  }, []);

  // 智能搜索功能实现
  const generateSearchSuggestions = useCallback(async (query: string): Promise<SearchSuggestion[]> => {
    if (!query.trim()) return [];

    const suggestions: SearchSuggestion[] = [];
    const lowercaseQuery = query.toLowerCase().trim();

    try {
      // 1. 订单号搜索 (最高优先级)
      const { data: orders } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          description,
          folder_name,
          order_files(count)
        `)
        .or(`order_number.ilike.%${lowercaseQuery}%,folder_name.ilike.%${lowercaseQuery}%`)
        .eq('is_active', true)
        .limit(3);

      if (orders?.length) {
        orders.forEach(order => {
          suggestions.push({
            id: `order-${order.id}`,
            type: 'order',
            title: `📋 订单: ${order.order_number}`,
            subtitle: order.description || `自动跳转到文件夹: ${order.folder_name}`,
            icon: <Package size={16} className="text-sky-600" />,
            data: order
          });
        });
      }

      // 2. 文件夹搜索 (次优先级)
      const folderSuggestions = categories
        .filter(category =>
          category.name.toLowerCase().includes(lowercaseQuery)
        )
        .slice(0, 3)
        .map(category => ({
          id: `folder-${category.id}`,
          type: 'folder' as const,
          title: `📁 文件夹: ${category.name}`,
          subtitle: `直接跳转到此分类`,
          icon: <Folder size={16} className="text-amber-600" />,
          data: category
        }));

      suggestions.push(...folderSuggestions);

      // 3. 文件搜索 (兜底方案)
      if (suggestions.length < 5) {
        suggestions.push({
          id: `file-search-${query}`,
          type: 'file',
          title: `📄 搜索文件: "${query}"`,
          subtitle: '打开高级搜索，在所有文件中查找',
          icon: <FileText size={16} className="text-slate-600" />,
          data: { query }
        });
      }

    } catch (error) {
      console.error('搜索建议生成失败:', error);
    }

    return suggestions.slice(0, 6);
  }, [categories]);

  // 防抖搜索
  const debouncedSearch = useCallback((query: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (query.trim()) {
        setIsSearching(true);
        const suggestions = await generateSearchSuggestions(query);
        setSearchSuggestions(suggestions);
        setShowSuggestions(true);
        setIsSearching(false);
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    }, 400);
  }, [generateSearchSuggestions]);

  // 处理搜索输入变化
  const handleSmartSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSmartSearchQuery(query);
    debouncedSearch(query);
  };

  // 处理搜索建议选择
  const handleSuggestionSelect = async (suggestion: SearchSuggestion) => {
    setSmartSearchQuery(suggestion.title);
    setShowSuggestions(false);

    switch (suggestion.type) {
      case 'order':
        try {
          const order = suggestion.data;
          if (profile?.id) {
            await supabase
              .from('order_user_access')
              .upsert({
                order_id: order.id,
                user_id: profile.id,
                accessed_at: new Date().toISOString()
              });
          }

          let categoryId: string | null = null;
          const { data: existingBinding } = await supabase
            .from('category_orders')
            .select('category_id, categories(id, name)')
            .eq('order_id', order.id)
            .single();

          if (existingBinding?.category_id) {
            categoryId = existingBinding.category_id;
          } else {
            const { data: existingCategory } = await supabase
              .from('categories')
              .select('id')
              .eq('name', order.folder_name)
              .single();

            if (existingCategory) {
              categoryId = existingCategory.id;
              await supabase
                .from('category_orders')
                .insert({
                  category_id: categoryId,
                  order_id: order.id,
                  binding_type: 'auto'
                });
            }
          }

          if (categoryId) {
            setTimeout(() => {
              navigate(`/uadmin/categories/${categoryId}`);
            }, 800);
            toast.addToast(`✅ 订单 ${order.order_number} 查询成功！正在跳转到文件夹...`, 'success', 1500);
          } else {
            toast.addToast(`⚠️ 订单 ${order.order_number} 查询成功！但未找到关联文件夹`, 'warning', 3000);
          }
        } catch (error) {
          console.error('订单查询失败:', error);
          toast.addToast('订单查询失败，请重试', 'error', 3000);
        }
        break;

      case 'folder':
        try {
          const category = suggestion.data;
          setTimeout(() => {
            navigate(`/uadmin/categories/${category.id}`);
          }, 300);
          toast.addToast(`🚀 正在跳转到文件夹: ${category.name}`, 'success', 1500);
        } catch (error) {
          console.error('文件夹跳转失败:', error);
          toast.addToast('文件夹跳转失败，请重试', 'error', 3000);
        }
        break;

      case 'file':
        setSearchOpen(true);
        useFileStore.getState().setSearchQuery(suggestion.data.query);
        break;
    }
  };

  // 键盘事件处理
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
      searchInputRef.current?.blur();
    } else if (e.key === 'Enter' && searchSuggestions.length > 0) {
      handleSuggestionSelect(searchSuggestions[0]);
    }
  };

  // 点击外部关闭建议框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionBoxRef.current &&
        !suggestionBoxRef.current.contains(event.target as Node) &&
        !searchInputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') return;
    const media = window.matchMedia('(min-width: 768px)');
    const listener = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    setIsDesktop(media.matches);
    try {
      media.addEventListener('change', listener);
      return () => media.removeEventListener('change', listener);
    } catch {
      media.addListener(listener);
      return () => media.removeListener(listener);
    }
  }, []);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (
        toolsMenuRef.current &&
        !toolsMenuRef.current.contains(event.target as Node) &&
        !toolsButtonRef.current?.contains(event.target as Node)
      ) {
        setShowToolsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuClick = () => {
    if (isDesktop) {
      toggleSidebar();
    } else {
      toggleMobileSidebar();
    }
  };

  // 构建文件夹的父级链
  const getCategoryChain = React.useCallback((categoryId: string | null): any[] => {
    if (!categoryId) return [];

    const chain: any[] = [];
    const flatCategories: any[] = [];

    // 扁平化所有分类
    const flatten = (cats: any[]) => {
      for (const cat of cats) {
        flatCategories.push(cat);
        if (cat.children?.length) flatten(cat.children);
      }
    };
    flatten(categories);

    // 从当前分类向上遍历
    let current = flatCategories.find(c => c.id === categoryId);
    while (current) {
      chain.unshift(current);
      if (!current.parent_id) break;
      current = flatCategories.find(c => c.id === current.parent_id);
    }

    return chain;
  }, [categories]);

  // 生成面包屑
  const getBreadcrumb = () => {
    const parts: { label: string; path?: string }[] = [];

    // 首页图标
    parts.push({ label: '', path: '/uadmin' });

    const pathname = location.pathname;

    // 根据路径添加面包屑
    if (pathname.includes('/categories') && activeCategory) {
      // 获取完整的文件夹层级链
      const chain = getCategoryChain(activeCategory.id);

      // 只显示上一级和当前级（如果层级很深的话）
      if (chain.length > 2) {
        // 上一级（可点击返回）
        const parent = chain[chain.length - 2];
        parts.push({
          label: parent.name,
          path: `/uadmin/categories/${parent.id}`
        });
      } else if (chain.length === 2) {
        // 只有两级：父级 + 当前
        const parent = chain[0];
        parts.push({
          label: parent.name,
          path: `/uadmin/categories/${parent.id}`
        });
      }
      // 如果只有一级（根文件夹），不显示额外的父级

      // 当前文件夹（不可点击，高亮显示）
      parts.push({ label: activeCategory.name });
    } else if (pathname.includes('/files/')) {
      // 文件详情页
      parts.push({ label: t('filesTitle'), path: '/uadmin/files' });
      parts.push({ label: t('fileInformation') });
    } else if (pathname.includes('/files')) {
      parts.push({ label: t('allFiles') });
    } else if (pathname.includes('/starred')) {
      parts.push({ label: t('starred') });
    } else if (pathname.includes('/recent')) {
      parts.push({ label: t('recent') });
    } else if (pathname.includes('/tags')) {
      parts.push({ label: t('tagsTitle') });
    } else if (pathname.includes('/trash')) {
      parts.push({ label: t('trashTitle') });
    } else if (pathname.includes('/orders')) {
      parts.push({ label: '订单管理' });
    } else if (pathname.includes('/faq-spaces')) {
      parts.push({ label: 'FAQ 页面管理' });
    } else if (pathname.includes('/users')) {
      parts.push({ label: '用户管理' });
    } else if (pathname.includes('/settings')) {
      parts.push({ label: t('settings') });
    } else if (pathname.includes('/help')) {
      parts.push({ label: t('helpCenter') });
    } else if (pathname === '/uadmin' || pathname === '/uadmin/') {
      // 首页不需要额外面包屑
    }

    return parts;
  };

  const breadcrumb = getBreadcrumb();

  // Tab items for filter
  const tabItems = FILE_TABS.map(tab => ({
    id: tab.id,
    label: tab.label,
  }));

  return (
    <header className="h-16 px-6 border-b border-slate-200 flex items-center justify-between bg-white flex-shrink-0 z-10">
      {/* Left: Menu + Breadcrumb */}
      <div className="flex items-center gap-4 flex-1">
        <button
          onClick={handleMenuClick}
          className="lg:hidden text-slate-500 hover:text-slate-700 transition-colors"
        >
          <Menu size={20} />
        </button>

        {/* Breadcrumb Navigation */}
        <nav className="flex items-center text-sm font-medium text-slate-500">
          {breadcrumb.map((item, index) => (
            <React.Fragment key={index}>
              {index === 0 ? (
                <a
                  href={item.path}
                  onClick={(e) => { e.preventDefault(); if (item.path) navigate(item.path); }}
                  className="hover:text-sky-500 transition-colors flex items-center gap-1"
                >
                  <Home size={18} />
                </a>
              ) : (
                <>
                  <ChevronRight size={16} className="mx-2 text-slate-300" />
                  {item.path ? (
                    <a
                      href={item.path}
                      onClick={(e) => { e.preventDefault(); navigate(item.path!); }}
                      className="hover:text-sky-500 transition-colors"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <span className="text-slate-800 font-semibold px-2 py-1 bg-slate-100 rounded">
                      {item.label}
                    </span>
                  )}
                </>
              )}
            </React.Fragment>
          ))}
        </nav>
      </div>

      {/* Center: Search */}
      <div className="flex-1 max-w-xl mx-6 hidden md:block">
        <div className="relative group">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={20} className={cn(
              "text-slate-400 group-focus-within:text-sky-500 transition-colors",
              isSearching && "text-sky-500 animate-pulse"
            )} />
          </span>
          <input
            ref={searchInputRef}
            type="text"
            value={smartSearchQuery}
            onChange={handleSmartSearchChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (smartSearchQuery && searchSuggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            className="block w-full pl-10 pr-12 py-2 border-none rounded-lg leading-5 bg-slate-100 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:bg-white transition-all text-sm"
            placeholder={t('searchPlaceholder') || '搜索订单、文件夹、文件...'}
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <span className="text-xs text-slate-400 border border-slate-300 rounded px-1.5 py-0.5">⌘K</span>
          </div>

          {/* Search Suggestions Dropdown */}
          {showSuggestions && (searchSuggestions.length > 0 || isSearching) && (
            <div
              ref={suggestionBoxRef}
              className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden"
            >
              {isSearching ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-5 h-5 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-3 text-sm text-slate-500">智能搜索中...</span>
                </div>
              ) : (
                <div className="py-2">
                  {searchSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      onClick={() => handleSuggestionSelect(suggestion)}
                      className="w-full flex items-center text-left px-4 py-2 hover:bg-slate-50 transition-colors group"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 mr-3 group-hover:scale-105 transition-transform">
                        {suggestion.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-slate-800 truncate">{suggestion.title}</div>
                        {suggestion.subtitle && (
                          <div className="text-xs text-slate-500 truncate">{suggestion.subtitle}</div>
                        )}
                      </div>
                      <ChevronRight size={16} className="text-slate-300" />
                    </button>
                  ))}
                  <div className="text-xs text-slate-400 px-4 py-2 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <span>回车搜索 • Esc 关闭</span>
                      <span className="text-sky-500">按优先级排序</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-3">
        {/* Filter Tabs - Desktop only */}
        <div className="hidden lg:flex items-center mr-2 space-x-1 text-sm font-medium bg-slate-100 p-1 rounded-lg">
          {tabItems.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveFileTab(tab.id as any);
                if (location.pathname !== '/uadmin' && location.pathname !== '/uadmin/') {
                  navigate('/uadmin');
                }
              }}
              className={cn(
                'px-3 py-1 rounded-md transition-all',
                activeFileTab === tab.id
                  ? 'bg-white shadow-sm text-slate-800'
                  : 'text-slate-500 hover:text-sky-500'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Smart Tools */}
        <div className="relative">
          <button
            ref={toolsButtonRef}
            onClick={() => setShowToolsMenu(!showToolsMenu)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-all duration-200',
              showToolsMenu
                ? 'bg-gradient-to-r from-sky-500 to-violet-500 text-white border-transparent shadow-md shadow-sky-200/50'
                : 'text-slate-600 border-slate-200 hover:border-sky-300 hover:text-sky-600 hover:bg-sky-50'
            )}
          >
            <Sparkles size={14} className={showToolsMenu ? 'animate-pulse' : ''} />
            <span className="hidden sm:inline">智能工具</span>
          </button>

          {showToolsMenu && (
            <div
              ref={toolsMenuRef}
              className="absolute right-0 mt-2 w-72 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden animate-fade-in"
            >
              {/* Header */}
              <div className="px-4 py-3 bg-gradient-to-r from-sky-50 to-violet-50 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-sky-500" />
                  <span className="text-sm font-semibold text-slate-800">智能工具</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">点击工具在新标签页中打开</p>
              </div>

              {/* Tool Cards */}
              <div className="p-2 space-y-1">
                {SMART_TOOLS.map((tool) => (
                  <button
                    key={tool.id}
                    onClick={() => {
                      window.open(tool.path, '_blank');
                      setShowToolsMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-all duration-150 group text-left"
                  >
                    <div className={cn(
                      'flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br shadow-sm',
                      tool.gradient
                    )}>
                      <tool.icon size={18} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 group-hover:text-sky-600 transition-colors">
                        {tool.name}
                      </div>
                      <div className="text-xs text-slate-400 truncate">{tool.description}</div>
                    </div>
                    <ExternalLink size={14} className="text-slate-300 group-hover:text-sky-400 transition-colors flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Language Toggle */}
        <button
          onClick={() => switchTo(lang === 'zh' ? 'en' : 'zh')}
          className="px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
        >
          {lang === 'zh' ? '中/EN' : 'EN/中'}
        </button>

        {/* User Avatar */}
        <div className="relative ml-2">
          <button
            ref={buttonRef}
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="focus:outline-none"
          >
            <img
              src={profile?.avatar_url || `https://ui-avatars.com/api/?name=${profile?.name || 'User'}&background=0EA5E9&color=fff`}
              alt={profile?.name || 'User profile'}
              className="h-9 w-9 rounded-full object-cover border border-slate-200 cursor-pointer hover:ring-2 hover:ring-offset-2 hover:ring-sky-500 transition-all"
            />
            <div className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-green-400 ring-2 ring-white"></div>
          </button>

          {showUserMenu && (
            <div
              ref={menuRef}
              className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-2 animate-fade-in"
            >
              <div className="px-4 py-2 border-b border-slate-100">
                <p className="font-semibold text-slate-800">{profile?.name || '用户'}</p>
                <p className="text-xs text-slate-500 mt-0.5">{profile?.email}</p>
              </div>
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  navigate('/uadmin/settings/profile');
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <Settings className="h-4 w-4 mr-3 text-slate-400" />
                {t('profile')}
              </button>
              <div className="border-t border-slate-100 mt-2 pt-2">
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    signOut();
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="h-4 w-4 mr-3" />
                  {t('signOut')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Search Modals */}
      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} categoryId={activeCategory?.id} />}
      <MobileSearchModal
        isOpen={mobileSearchOpen}
        onClose={() => setMobileSearchOpen(false)}
      />
    </header>
  );
};

export default HeaderNew;
